import type { NativeF0Frame } from '@muzly/shared';

import type { MelodyNote } from '../melody';

/**
 * takeGraphSvg — 테이크 **전체**를 한 장에 그린 디버그용 그림.
 *
 * 화면의 그래프(`PitchTraceGraph`)는 10초 창을 밀어 가며 보여 줘서, "그래프가 튀었다"는
 * 제보를 받아도 어느 프레임이 어떻게 그려졌는지 되짚을 수가 없다. 이 그림은 같은 테이크의
 * WAV 옆에 올라가(`mediaUpload`) 녹음·프레임·화면을 한 자리에서 맞춰 보게 한다.
 *
 * 화면과 같은 그림이 아니다 — 화면이 숨기는 것을 같이 보여 주는 것이 목적이다:
 *   - 점 = 원시 `f0Hz`(채점이 읽는 값). 색이 신뢰도다.
 *   - 선 = `smoothedF0Hz`(화면이 그리는 값).
 *   - 회색 막대 = 가이드 멜로디. 시간 원점은 테이크 첫 프레임이다(`scoreSong` 과 같은 앵커).
 * 둘이 갈라진 자리가 곧 표시 단계(post-processor)가 개입한 자리다.
 *
 * 순수 함수다. DOM·canvas 를 안 쓰므로 래스터화도 없다 — SVG 는 브라우저가 그대로 연다.
 */

const PX_PER_SEC = 100;
const PX_PER_SEMITONE = 12;
const PAD = { left: 44, right: 12, top: 12, bottom: 24 };
/** 데이터가 없을 때 세로축이 잡는 범위(C3–C5). */
const DEFAULT_MIDI = { lo: 48, hi: 72 };

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
];

const noteName = (midi: number): string =>
  `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

const midiOfHz = (hz: number): number => 69 + 12 * Math.log2(hz / 440);

const fmt = (n: number): number => Math.round(n * 10) / 10;

/** 신뢰도 색 — 0.85 는 post-processor 의 상대 기준선(`kConfRelTol`)과 같은 눈금이다. */
const confColor = (confidence: number): string =>
  confidence >= 0.85 ? '#16a34a' : confidence >= 0.6 ? '#f59e0b' : '#dc2626';

export interface TakeGraphInput {
  frames: NativeF0Frame[];
  melody?: MelodyNote[];
}

export const takeGraphSvg = ({
  frames,
  melody = [],
}: TakeGraphInput): string => {
  const startMs = frames.length > 0 ? frames[0].timestampMs : 0;
  const endMs = Math.max(
    frames.length > 0 ? frames[frames.length - 1].timestampMs - startMs : 0,
    ...melody.map((note) => note.startMs + note.durMs),
    1000,
  );

  const raw = frames.flatMap((f) =>
    f.voiced && f.f0Hz != null && f.f0Hz > 0
      ? [
          {
            t: f.timestampMs - startMs,
            midi: midiOfHz(f.f0Hz),
            confidence: f.confidence,
          },
        ]
      : [],
  );
  const shown = frames.map((f) =>
    f.smoothedF0Hz != null && f.smoothedF0Hz > 0
      ? { t: f.timestampMs - startMs, midi: midiOfHz(f.smoothedF0Hz) }
      : null,
  );

  const midis = [
    ...raw.map((p) => p.midi),
    ...shown.flatMap((p) => (p ? [p.midi] : [])),
    ...melody.map((note) => note.midi),
  ];
  const lo =
    midis.length > 0 ? Math.floor(Math.min(...midis)) - 2 : DEFAULT_MIDI.lo;
  const hi =
    midis.length > 0 ? Math.ceil(Math.max(...midis)) + 2 : DEFAULT_MIDI.hi;

  const plotW = Math.ceil((endMs / 1000) * PX_PER_SEC);
  const plotH = (hi - lo) * PX_PER_SEMITONE;
  const width = PAD.left + plotW + PAD.right;
  const height = PAD.top + plotH + PAD.bottom;
  const x = (tMs: number): number => fmt(PAD.left + (tMs / 1000) * PX_PER_SEC);
  const y = (midi: number): number =>
    fmt(PAD.top + (hi - midi) * PX_PER_SEMITONE);

  const grid: string[] = [];
  for (let m = lo; m <= hi; m += 1) {
    const isC = ((m % 12) + 12) % 12 === 0;
    grid.push(
      `<line x1="${PAD.left}" x2="${PAD.left + plotW}" y1="${y(m)}" y2="${y(m)}" stroke="${isC ? '#9ca3af' : '#e5e7eb'}" stroke-width="${isC ? 1 : 0.5}"/>`,
    );
    if (isC || m === lo || m === hi) {
      grid.push(
        `<text x="${PAD.left - 4}" y="${y(m) + 3}" text-anchor="end">${noteName(m)} ${m}</text>`,
      );
    }
  }
  for (let s = 0; s * 1000 <= endMs; s += 1) {
    grid.push(
      `<line x1="${x(s * 1000)}" x2="${x(s * 1000)}" y1="${PAD.top}" y2="${PAD.top + plotH}" stroke="#e5e7eb" stroke-width="0.5"/>`,
      `<text x="${x(s * 1000)}" y="${PAD.top + plotH + 14}" text-anchor="middle">${s}s</text>`,
    );
  }

  const guide = melody.map(
    (note) =>
      `<rect x="${x(note.startMs)}" y="${fmt(y(note.midi) - PX_PER_SEMITONE / 2)}" width="${fmt((note.durMs / 1000) * PX_PER_SEC)}" height="${PX_PER_SEMITONE}" fill="#9ca3af" fill-opacity="0.45"/>`,
  );

  // 표시값이 끊긴 자리(null)에서 선도 끊는다 — 이으면 "안 그린 구간"이 그린 것처럼 보인다.
  const lines: string[] = [];
  let run: string[] = [];
  for (const p of [...shown, null]) {
    if (p) {
      run.push(`${x(p.t)},${y(p.midi)}`);
    } else if (run.length > 0) {
      // 점 하나짜리 구간은 polyline 으로는 안 보인다.
      lines.push(
        run.length === 1
          ? `<circle cx="${run[0].split(',')[0]}" cy="${run[0].split(',')[1]}" r="1.5" fill="#2563eb"/>`
          : `<polyline points="${run.join(' ')}" fill="none" stroke="#2563eb" stroke-width="1.5"/>`,
      );
      run = [];
    }
  }

  const dots = raw.map(
    (p) =>
      `<circle cx="${x(p.t)}" cy="${y(p.midi)}" r="1.6" fill="${confColor(p.confidence)}"/>`,
  );

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="monospace" font-size="9" fill="#374151">`,
    `<rect width="${width}" height="${height}" fill="#ffffff"/>`,
    ...grid,
    ...guide,
    ...lines,
    ...dots,
    '</svg>',
  ].join('\n');
};
